import { Fragment, useEffect, useState } from 'react';
import type { FlowDoc, FlowStep } from '../types/global';
import type { PinnedAgent } from '../lib/pinnedAgents';
import { AgentSpecView, hasSpecContent } from './AgentSpecView';

function scheduleLabel(schedule: PinnedAgent['schedule']): string {
  switch (schedule) {
    case 'every_10s': return 'every 10s';
    case 'every_1m': return 'every 1m';
    case 'every_10m': return 'every 10m';
    case 'every_1h': return 'every 1h';
    default: return '';
  }
}

function intervalLabelSeconds(seconds: number): string {
  if (seconds % 3600 === 0) return `every ${seconds / 3600}h`;
  if (seconds % 60 === 0) return `every ${seconds / 60}m`;
  return `every ${seconds}s`;
}

function runCadenceLabel(
  schedule: PinnedAgent['schedule'],
  tickIntervalSeconds: number | null | undefined,
): string {
  if (tickIntervalSeconds != null && tickIntervalSeconds > 0) {
    return intervalLabelSeconds(tickIntervalSeconds);
  }
  return scheduleLabel(schedule);
}

function formatRelativeTime(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return '';
  const deltaSec = Math.max(0, Math.floor(Date.now() / 1000 - unixSeconds));
  if (deltaSec < 60) return `ran ${deltaSec}s ago`;
  if (deltaSec < 3600) return `ran ${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `ran ${Math.floor(deltaSec / 3600)}h ago`;
  return `ran ${Math.floor(deltaSec / 86400)}d ago`;
}

// Renders an Agent Factory workflow diagram from a FlowDoc — the JSON
// document the agent emits in agent-factory mode. Visual style mirrors
// /example/online: a vertical spine of nodes with connectors and an optional
// fork branch off a yes/no gate.

function StepNode({ step, onClick }: { step: FlowStep; onClick?: (step: FlowStep) => void }) {
  const classes = ['node'];
  if (step.kind === 'gate') classes.push('gate');
  if (step.dashed) classes.push('dashed');
  return (
    <button type="button" className={classes.join(' ')} onClick={onClick ? () => onClick(step) : undefined}>
      {step.chip ? (
        <span className={step.kind === 'trigger' ? 'chip on' : 'chip'}>{step.chip}</span>
      ) : null}
      <span className="txt">{step.text}</span>
      {step.loop ? <i className="loop">{step.loop}</i> : null}
    </button>
  );
}

function Link() {
  return (
    <div className="link">
      <span className="ln" />
      <span className="ah" />
    </div>
  );
}

function ForkBlock({ fork, onSelectStep }: { fork: FlowStep; onSelectStep: (step: FlowStep) => void }) {
  return (
    <>
      <div className="split">
        <span className="vtop" />
        <span className="hbar" />
        <span className="vl" />
        <span className="vr" />
        {fork.leftLabel ? <span className="cond l">{fork.leftLabel}</span> : null}
        {fork.rightLabel ? <span className="cond r">{fork.rightLabel}</span> : null}
      </div>
      <div className="fork">
        <div className="col">
          <span className="ah" />
          {fork.side ? <StepNode step={fork.side} onClick={onSelectStep} /> : null}
          <span className="ln tail" />
        </div>
        <div className="col">
          <span className="ln grow" />
        </div>
      </div>
      <div className="merge">
        <span className="hbar" />
        <span className="vbot" />
        <span className="ah" />
      </div>
    </>
  );
}

function kindLabel(kind: FlowStep['kind']): string {
  switch (kind) {
    case 'trigger': return 'Trigger';
    case 'gate': return 'Decision';
    case 'fork': return 'Branch';
    default: return 'Step';
  }
}

function StepPopup({ step, onClose }: { step: FlowStep; onClose: () => void }) {
  return (
    <div className="factory-popup-backdrop" onClick={onClose} role="presentation">
      <div className="factory-popup" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button type="button" className="factory-popup-close" onClick={onClose} aria-label="Close">×</button>
        <div className="factory-popup-kind">{kindLabel(step.kind)}</div>
        {step.chip ? <div className="factory-popup-chip">{step.chip}</div> : null}
        <div className="factory-popup-text">{step.text || (step.kind === 'fork' ? 'Branch' : '')}</div>
        {step.leftLabel || step.rightLabel ? (
          <div className="factory-popup-meta">
            {step.leftLabel ? <span><b>Left:</b> {step.leftLabel}</span> : null}
            {step.rightLabel ? <span><b>Right:</b> {step.rightLabel}</span> : null}
          </div>
        ) : null}
        {step.side ? (
          <div className="factory-popup-meta">
            <span><b>Side branch:</b> {step.side.text}</span>
          </div>
        ) : null}
        {step.loop ? (
          <div className="factory-popup-meta">
            <span><b>Loop:</b> {step.loop}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function tickRelativeTime(unixSeconds: number): string {
  const deltaSec = Math.max(0, Math.floor(Date.now() / 1000 - unixSeconds));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return 'yesterday';
}

type TickEntry = { at: number; status: 'ok' | 'error' | 'skipped'; message: string };

function RecentTicksPanel({
  tickHistory,
  tickIntervalSeconds,
  tickMaxRuntimeSeconds,
}: {
  tickHistory?: TickEntry[];
  tickIntervalSeconds?: number;
  tickMaxRuntimeSeconds?: number;
}) {
  const [now, setNow] = useState(() => Date.now());

  // Re-render every 5s so relative timestamps stay fresh.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(id);
  }, []);

  // Suppress the TS "unused variable" on `now` — it's only here to trigger re-renders.
  void now;

  const entries = (tickHistory ?? []).slice().reverse().slice(0, 10);

  function intervalLabel(seconds: number): string {
    if (seconds % 3600 === 0) return `every ${seconds / 3600}h`;
    if (seconds % 60 === 0) return `every ${seconds / 60}m`;
    return `every ${seconds}s`;
  }

  return (
    <div className="factory-ticks">
      <div className="factory-ticks-header">
        <span className="factory-ticks-title">Recent ticks</span>
        {tickIntervalSeconds != null && (
          <span className="factory-ticks-meta">{intervalLabel(tickIntervalSeconds)}</span>
        )}
        {tickMaxRuntimeSeconds != null && (
          <span className="factory-ticks-meta">max {tickMaxRuntimeSeconds}s</span>
        )}
      </div>
      {entries.length === 0 ? (
        <div className="factory-ticks-empty">No ticks yet</div>
      ) : (
        <ul className="factory-ticks-list">
          {entries.map((entry, i) => (
            <li key={i} className="factory-ticks-entry">
              <span className={`factory-ticks-pill factory-ticks-pill-${entry.status}`}>
                {entry.status}
              </span>
              <span className="factory-ticks-time">{tickRelativeTime(entry.at)}</span>
              <span className="factory-ticks-msg" title={entry.message}>
                {entry.message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function FactoryCanvas({
  flow,
  onMakeAgent,
  onPinAgent,
  canMakeAgent,
  canPinAgent,
  builtinPinId,
  builtinPinned,
  onToggleBuiltinPin,
  canEditAgent,
  isModifiedFromImplementation,
  onEditAgent,
  onClose,
  runEnabled,
  runSchedule,
  runLastRunAt,
  runLastRunStatus,
  runLastRunMessage,
  onToggleRunning,
  tickHistory,
  tickIntervalSeconds,
  tickMaxRuntimeSeconds,
}: {
  flow: FlowDoc | null;
  onMakeAgent?: (flow: FlowDoc) => Promise<void> | void;
  onPinAgent?: (flow: FlowDoc) => void;
  canMakeAgent?: boolean;
  canPinAgent?: boolean;
  // When viewing a built-in agent's own diagram, this is its pin id — renders
  // a Pin/Unpin toggle above the diagram. Built-ins aren't pinned by default;
  // this is how the user adds/removes them from the sidebar.
  builtinPinId?: string | null;
  builtinPinned?: boolean;
  onToggleBuiltinPin?: (id: string, pinned: boolean) => void;
  // True when the active chat is editing a built-in pin (e.g. Agent Factory).
  // When the chat's latest emitted flow matches the pin we just show a
  // "Synced with implementation" hint; when it differs we show the
  // "Modified from implementation" indicator and the Edit agent button.
  canEditAgent?: boolean;
  isModifiedFromImplementation?: boolean;
  onEditAgent?: (flow: FlowDoc) => void;
  onClose?: () => void;
  // Runner metadata for the agent currently being viewed. When omitted (or
  // when `runSchedule === 'on_demand'`), the start/stop row is hidden.
  runEnabled?: boolean;
  runSchedule?: PinnedAgent['schedule'];
  runLastRunAt?: number | null;
  runLastRunStatus?: PinnedAgent['last_run_status'];
  runLastRunMessage?: string | null;
  onToggleRunning?: (enabled: boolean) => void;
  // Tick history fields — shown only when the agent has a schedule or interval.
  tickHistory?: TickEntry[];
  tickIntervalSeconds?: number;
  tickMaxRuntimeSeconds?: number;
}) {
  const [selectedStep, setSelectedStep] = useState<FlowStep | null>(null);
  const [isMaking, setIsMaking] = useState(false);
  const [makeSuccess, setMakeSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSpec, setShowSpec] = useState(false);

  const specAvailable = hasSpecContent(flow?.spec);
  // A diagram swap (new chat / new emit) shouldn't strand the user on a stale
  // brief — fall back to the diagram whenever the spec disappears.
  useEffect(() => {
    if (!specAvailable) setShowSpec(false);
  }, [specAvailable]);

  // The optional GPT-image visualization the agent-factory turn generated in
  // parallel with the JSON diagram. When set, the canvas shows a small
  // Diagram | Visual tab toggle and Visual swaps the spine for the image.
  const imageUrl = flow?.imageUrl?.trim() || '';
  const [view, setView] = useState<'diagram' | 'visual'>('diagram');
  useEffect(() => { if (!imageUrl) setView('diagram'); }, [imageUrl]);

  const briefButton = specAvailable ? (
    <button
      type="button"
      className="factory-canvas-brief"
      onClick={() => setShowSpec(true)}
      title="View the agent brief — your request, a clarified rewrite, and a detailed spec"
      aria-label="View the agent brief"
    >
      <i className="fas fa-clipboard-list" aria-hidden="true" />
    </button>
  ) : null;

  const handleMakeClick = async () => {
    if (!onMakeAgent || !flow) return;
    setIsMaking(true);
    setMakeSuccess(false);
    try {
      await onMakeAgent(flow);
      setMakeSuccess(true);
      setTimeout(() => setMakeSuccess(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setIsMaking(false);
    }
  };

  const closeButton = onClose ? (
    <button
      type="button"
      className="factory-canvas-close"
      onClick={onClose}
      title="Close diagram — back to chat"
      aria-label="Close diagram"
    >
      <i className="fas fa-times" aria-hidden="true" />
    </button>
  ) : null;

  if (!flow || !flow.steps?.length) {
    return (
      <div className="factory-canvas factory-canvas-empty">
        {closeButton}
        <div className="wrap">
          <div className="eyebrow">Agent Factory</div>
          <h1>No diagram yet</h1>
          <p className="desc">
            Ask the agent in the panel on the right to design a workflow — for example,
            <i> &ldquo;Design a workflow for onboarding a new customer.&rdquo;</i> The diagram
            will appear here as the agent produces it.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="factory-canvas">
      {closeButton}
      {briefButton}
      <div className="wrap">
        <header>
          {flow.eyebrow ? <div className="eyebrow">{flow.eyebrow}</div> : null}
          <h1>{flow.title}</h1>
          {flow.description ? <p className="desc">{flow.description}</p> : null}
          {runSchedule && runSchedule !== 'on_demand' && onToggleRunning ? (
            <div className="factory-run-row">
              <button
                type="button"
                className={`factory-run-toggle${runEnabled ? ' running' : ''}`}
                onClick={() => onToggleRunning(!runEnabled)}
                title={runEnabled ? 'Stop this agent' : 'Start this agent'}
              >
                <i
                  className={`fas ${runEnabled ? 'fa-pause' : 'fa-play'}`}
                  aria-hidden="true"
                />
                {runEnabled ? 'Stop' : 'Start'}
              </button>
              <span className="factory-run-status">
                {runEnabled ? `Running ${runCadenceLabel(runSchedule, tickIntervalSeconds)}` : 'Stopped'}
              </span>
              {runLastRunAt ? (
                <span className="factory-run-meta">{formatRelativeTime(runLastRunAt)}</span>
              ) : null}
              {runLastRunStatus === 'error' ? (
                <span
                  className="factory-run-error"
                  title={runLastRunMessage || 'Last run errored'}
                >
                  <span className="factory-run-error-dot" aria-hidden="true" />
                  {runLastRunMessage
                    ? runLastRunMessage.slice(0, 60) + (runLastRunMessage.length > 60 ? '…' : '')
                    : 'error'}
                </span>
              ) : null}
            </div>
          ) : null}
          {canEditAgent && onEditAgent ? (
            <div className="factory-agent-actions factory-edit-actions">
              {isModifiedFromImplementation ? (
                <span
                  className="factory-modified-indicator"
                  title="The diagram in this chat differs from the agent's current implementation"
                >
                  <i className="fas fa-exclamation-circle" aria-hidden="true" />
                  Modified from implementation
                </span>
              ) : (
                <span className="factory-modified-indicator synced" title="Diagram matches the agent's implementation">
                  <i className="fas fa-check-circle" aria-hidden="true" />
                  Matches implementation
                </span>
              )}
              <button
                type="button"
                className="factory-edit-agent"
                onClick={() => {
                  if (!flow || !onEditAgent || isEditing) return;
                  setIsEditing(true);
                  try {
                    onEditAgent(flow);
                  } finally {
                    // The button stays in "dispatched" state until the chat
                    // mode changes; the dev turn runs async in the chat panel.
                    setTimeout(() => setIsEditing(false), 1500);
                  }
                }}
                disabled={!isModifiedFromImplementation || isEditing}
                title={isModifiedFromImplementation
                  ? "Apply the new diagram to the agent's implementation"
                  : 'No changes yet — iterate the diagram in the chat first'}
              >
                {isEditing ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Dispatching...
                  </>
                ) : (
                  <>
                    <i className="fas fa-wrench" aria-hidden="true" /> Edit agent
                  </>
                )}
              </button>
            </div>
          ) : canMakeAgent && onMakeAgent ? (
            <div className="factory-agent-actions">
              <button
                type="button"
                className={`factory-make-agent${makeSuccess ? ' success' : ''}`}
                onClick={handleMakeClick}
                disabled={isMaking}
                title="Implement this agent: pin it, dispatch an Alty Dev turn to write the impl doc + any net-new code, and rename the chat after the diagram title"
              >
                {isMaking ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Making agent...
                  </>
                ) : makeSuccess ? (
                  <>
                    <i className="fas fa-check-circle" aria-hidden="true" /> Agent made!
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" aria-hidden="true" /> Make agent
                  </>
                )}
              </button>
              {canPinAgent && onPinAgent ? (
                <button
                  type="button"
                  className="factory-pin-agent"
                  onClick={() => onPinAgent(flow)}
                  title="Pin this agent to the top of the chat sidebar"
                >
                  <i className="fas fa-thumbtack" aria-hidden="true" /> Pin
                </button>
              ) : null}
            </div>
          ) : null}
          {builtinPinId && onToggleBuiltinPin ? (
            <div className="factory-agent-actions">
              <button
                type="button"
                className="factory-pin-agent"
                onClick={() => onToggleBuiltinPin(builtinPinId, !builtinPinned)}
                title={builtinPinned
                  ? 'Unpin this agent from the chat sidebar'
                  : 'Pin this agent to the top of the chat sidebar'}
              >
                <i className="fas fa-thumbtack" aria-hidden="true" /> {builtinPinned ? 'Unpin' : 'Pin'}
              </button>
            </div>
          ) : null}
        </header>
        <div className="rule" />
        {imageUrl ? (
          <div className="factory-view-tabs" role="tablist" aria-label="Diagram view">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'diagram'}
              className={`factory-view-tab${view === 'diagram' ? ' on' : ''}`}
              onClick={() => setView('diagram')}
              title="Spine diagram"
            >
              <i className="fas fa-sitemap" aria-hidden="true" /> Diagram
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'visual'}
              className={`factory-view-tab${view === 'visual' ? ' on' : ''}`}
              onClick={() => setView('visual')}
              title="ChatGPT Image rendering of the same workflow"
            >
              <i className="fas fa-image" aria-hidden="true" /> Visual
            </button>
          </div>
        ) : null}
        {view === 'visual' && imageUrl ? (
          <figure className="factory-visual">
            <img src={imageUrl} alt={`Visual rendering of ${flow.title || 'agent'}`} />
          </figure>
        ) : (
          <div className="flow">
            {flow.steps.map((step, index) => {
              const prev = flow.steps[index - 1];
              const needsLink = prev && prev.kind !== 'gate' && prev.kind !== 'fork';
              return (
                <Fragment key={index}>
                  {needsLink ? <Link /> : null}
                  {step.kind === 'fork'
                    ? <ForkBlock fork={step} onSelectStep={setSelectedStep} />
                    : <StepNode step={step} onClick={setSelectedStep} />}
                </Fragment>
              );
            })}
          </div>
        )}
        {(tickIntervalSeconds != null || (runSchedule && runSchedule !== 'on_demand')) ? (
          <RecentTicksPanel
            tickHistory={tickHistory}
            tickIntervalSeconds={tickIntervalSeconds}
            tickMaxRuntimeSeconds={tickMaxRuntimeSeconds}
          />
        ) : null}
      </div>
      {selectedStep ? <StepPopup step={selectedStep} onClose={() => setSelectedStep(null)} /> : null}
      {showSpec && flow?.spec ? (
        <AgentSpecView spec={flow.spec} onBack={() => setShowSpec(false)} />
      ) : null}
    </div>
  );
}
